import React, { useImperativeHandle } from 'react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import reportslogo from "../../../assets/images/reportslogo.png"; // Safely import your local logo file

const ViewAssetMedicalRiskChecklist = React.forwardRef(({ rows, data }, ref) => {
    
    useImperativeHandle(ref, () => ({
        async generatePDF() {
            const assetList = Array.isArray(rows) ? rows : [];
            const riskName = data?.riskStatus.value === "All" ? "High/Moderate" : data?.riskStatus.value === "High Risk" ? "High" : "Moderate";
            const hospitalName = rows?.[0]?.hospitalName || "PCMC";

            // 1. Initialize Portrait Document
            const pdf = new jsPDF('p', 'mm', 'a4'); 
            const pageWidth = pdf.internal.pageSize.getWidth(); // 210mm
            const marginX = 8;
            const contentWidth = pageWidth - (marginX * 2); // 194mm printable area

            // Helper function to convert your imported image file to a drawable object inside jsPDF
            const loadLogoImage = (src) => {
                return new Promise((resolve) => {
                    const img = new Image();
                    img.src = src;
                    img.onload = () => resolve(img);
                    img.onerror = () => resolve(null); // Fallback gracefully if image path is broken
                });
            };

            const logoImgElement = await loadLogoImage(reportslogo);

            // 2. Custom Brand Header Banner Layout
            pdf.setFillColor(122, 28, 28); // #7A1C1C Burgundy Maroon
            pdf.rect(marginX, 10, contentWidth, 16, 'F');

            // Draw the corporate branding logo on the LEFT side of the banner
            if (logoImgElement) {
                const logoWidth = 20; // Size constraints in millimeters
                const logoHeight = 10; 
                const logoX = marginX + 4; // Shifted flush against the left boundary margin
                const logoY = 13;
                pdf.addImage(logoImgElement, 'PNG', logoX, logoY, logoWidth, logoHeight);
            }

          // Clean centering alignment for Title text directly down the middle of the document page
            pdf.setTextColor(255, 255, 255);
            pdf.setFont("Helvetica", "bold");
            pdf.setFontSize(11);
            pdf.text(
                "MEDICAL DEVICE RISK EVALUATION CHECKLIST REPORT", 
                pageWidth / 2, // Centered horizontal coordinate
                20, 
                { align: "center" } // Auto-centers calculation logic matching text length bounds
            );

            // 3. Subtitle Block
            pdf.setTextColor(27, 79, 114); // #1B4F72 Navy Blue
            pdf.setFontSize(10);
            pdf.text(`${riskName} Risk Level`, pageWidth / 2, 32, { align: "center" });

            // 4. Executive Summary Block (CORRECTED: Heading has background, content block is clean/transparent)
            let summaryY = 38;
            const headingHeight = 7;
            
            // Paint Heading Background (Light Gray Background Banner)
            pdf.setFillColor(230, 230, 230); // #E6E6E6
            pdf.rect(marginX, summaryY, contentWidth, headingHeight, 'F');

            // Write Executive Summary Heading Text Inside the Gray Banner
            pdf.setTextColor(51, 51, 51);
            pdf.setFont("Helvetica", "bold");
            pdf.setFontSize(9.5);
            pdf.text("Executive Summary", marginX + 2, summaryY + 5);
            
            // Write Executive Summary Body Description Text below (No background color box rendered here)
            pdf.setFont("Helvetica", "normal");
            pdf.setFontSize(8.5);
            pdf.setTextColor(85, 85, 85);
            
            const summaryText = "This report summarises the Medical Device Evaluation Checklist conducted for medical devices classified as High Risk under the Medical Device Risk Assessment framework. Each asset is assessed based on six risk criteria including patient impact, safety or performance failure, hazard exposure, repair cost, physical condition, and breakdown frequency. The evaluation helps determine the current condition of high-risk assets and supports monitoring, maintenance intervention, and procurement planning.";
            const splitSummary = pdf.splitTextToSize(summaryText, contentWidth); // Full horizontal text bounds allocation
            
            pdf.text(splitSummary, marginX, summaryY + headingHeight + 5);

            // Dynamically calculate where this section finishes to move down layout tracking pointer
            const summaryLineCount = splitSummary.length;
            let currentY = summaryY + headingHeight + 5 + (summaryLineCount * 4) + 4;

            // --- 5. Asset Condition Classification Section ---
            // Section Header Gray Banner
            pdf.setFillColor(230, 230, 230); // Light Gray Background
            pdf.rect(marginX, currentY, contentWidth, 7, 'F');
            pdf.setTextColor(51, 51, 51);
            pdf.setFont("Helvetica", "bold");
            pdf.setFontSize(9.5);
            pdf.text("Asset Condition Classification", marginX + 2, currentY + 5);
            currentY += 12;

            // Rule 1 Text Blocks
            pdf.setTextColor(31, 97, 141); // Blue heading color (#1F618D)
            pdf.setFontSize(8.5);
            pdf.text("A. Rule 1: Critical Risk Trigger (Auto Poor Condition)", marginX, currentY);
            currentY += 4;

            pdf.setTextColor(51, 51, 51);
            pdf.setFont("Helvetica", "normal");
            pdf.text("If any one of the following critical criteria is selected, the asset will be automatically classified as Poor Condition:", marginX, currentY);
            currentY += 4;

            // Numbered items for Rule 1
            pdf.text("1. Patient Impact Risk", marginX + 4, currentY); currentY += 4;
            pdf.text("2. Hazard Risk", marginX + 4, currentY); currentY += 4;
            pdf.text("3. Failed Safety or Performance Requirement", marginX + 4, currentY);
            currentY += 8;

            // Rule 2 Text Blocks
            pdf.setTextColor(31, 97, 141); // Blue heading color (#1F618D)
            pdf.setFont("Helvetica", "bold");
            pdf.text("B. Rule 2: Tick Count Evaluation", marginX, currentY);
            currentY += 4;

            pdf.setTextColor(51, 51, 51);
            pdf.setFont("Helvetica", "normal");
            pdf.text("If Rule 1 is not triggered, the asset condition will be determined based on the number of risk criteria selected.", marginX, currentY);
            currentY += 4;

            // Render Rule 2 Matrix Table with explicit grid borders
            pdf.autoTable({
                startY: currentY,
                margin: { left: marginX, right: marginX },
                head: [['Criteria Selected', 'Asset Condition']],
                body: [
                    ['>= 3 criteria', 'Poor'], // Maps natively to character glyph '≥' via inner symbol hooks
                    ['< 3 criteria', 'Fair'],
                    ['0 criteria', 'Good']
                ],
                theme: 'grid', 
                styles: {
                    fontSize: 8.5,
                    cellPadding: 3,
                    lineWidth: 0.2,
                    lineColor: [191, 191, 191], // Gray borders (#BFBFBF)
                    halign: 'left',
                    textColor: [51, 51, 51],
                    font: "Helvetica"
                },
                headStyles: {
                    fillColor: [217, 225, 242], // Light blue-gray background (#D9E1F2)
                    textColor: [51, 51, 51],
                    fontStyle: 'bold',
                    lineWidth: 0.2,
                    lineColor: [191, 191, 191]
                },
                columnStyles: {
                    0: { cellWidth: contentWidth / 2 },
                    1: { cellWidth: contentWidth / 2 }
                },
                willDrawCell: function (tableData) {
                    // Temporarily toggles core font rules ONLY for the row row cells tracking math values
                    if (tableData.cell.section === 'body' && tableData.column.index === 0 && tableData.cell.text[0].startsWith('³')) {
                        tableData.doc.setFont('Symbol', 'normal');
                    }
                },
                didParseCell: function (tableData) {
                    if (tableData.column.index === 1 && tableData.cell.section === 'body') {
                        if (tableData.cell.text[0] === 'Poor') {
                            tableData.cell.styles.textColor = [255, 0, 0]; // Red text
                            tableData.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            });

            // Get bottom coordinate of Classification table to position the main table safely below it
            currentY = pdf.lastAutoTable.finalY + 10;

            // 6. Columns Configuration for Main Data Table
            const mainColumns = [
                { header: 'Hospital Name', dataKey: 'hospitalName' },
                { header: 'ASSET NO.', dataKey: 'assetNo' },
                { header: 'ASSET DESC', dataKey: 'assetDescription' },
                { header: 'ASSET AGE', dataKey: 'assetAge' },
                { header: 'S/N MODEL', dataKey: 'serialNo' },
                { header: 'DEPT DESC', dataKey: 'departmentDescription' },
                { header: 'PATIENT IMPACT RISK', dataKey: 'patientimpactrisk' },
                { header: 'FAILED SAFETY / PERFORMANCE', dataKey: 'failedsafetyperformance' },
                { header: 'HAZARD RISK', dataKey: 'hazardrisk' },
                { header: 'HIGH REPAIR COST', dataKey: 'highrepaircost' },
                { header: 'BAD PHYSICAL CONDITION', dataKey: 'badphysicalcondition' },
                { header: 'BREAKDOWN FREQ >5 TIMES/YEAR', dataKey: 'breakdownfreq' },
                { header: 'ASSET CONDITION', dataKey: 'assetCondition' }
            ];

            // 7. Generate Main Data Grid Rows with clear inner column divider borders
            pdf.autoTable({
                startY: currentY,
                columns: mainColumns,
                body: assetList,
                theme: 'grid', // Changed to grid theme to enable visible column-by-column borders
                margin: { top: 12, right: marginX, bottom: 12, left: marginX },
                styles: {
                    fontSize: 6.5,
                    font: "Helvetica",
                    cellPadding: 1.2,
                    halign: 'center',
                    valign: 'middle',
                    overflow: 'linebreak',
                    lineWidth: 0.15,
                    lineColor: [166, 172, 175] // #A6ACAF Border Color matching original stylesheet
                },
                headStyles: {
                    fillColor: [91, 30, 30], // #5B1E1E Dark Brownish Red
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 5.5,
                    cellPadding: 1,
                    lineWidth: 0.15,
                    lineColor: [166, 172, 175]
                },
                columnStyles: {
                    hospitalName: { halign: 'left', cellWidth: 10 },
                    assetNo: { cellWidth: 20 },
                    assetDescription: { halign: 'left', cellWidth: 23 },
                    assetAge: { cellWidth: 10 },
                    serialNo: { cellWidth: 15 },
                    departmentDescription: { halign: 'left', cellWidth: 20 },
                    patientimpactrisk: { cellWidth: 13 },
                    failedsafetyperformance: { cellWidth: 15 },
                    hazardrisk: { cellWidth: 12 },
                    highrepaircost: { cellWidth: 11 },
                    badphysicalcondition: { cellWidth: 13 },
                    breakdownfreq: { cellWidth: 15 },
                    assetCondition: { cellWidth: 18 }
                },
                didParseCell: function (tableData) {
                    if (tableData.column.dataKey === 'assetCondition' && tableData.cell.section === 'body') {
                        const val = String(tableData.cell.raw).toUpperCase();
                        if (val === 'GOOD') {
                            tableData.cell.styles.fillColor = [232, 248, 245];
                            tableData.cell.styles.textColor = [14, 90, 86];
                            tableData.cell.styles.fontStyle = 'bold';
                        } else if (val === 'FAIR') {
                            tableData.cell.styles.fillColor = [254, 249, 231];
                            tableData.cell.styles.textColor = [183, 149, 11];
                            tableData.cell.styles.fontStyle = 'bold';
                        } else {
                            tableData.cell.styles.fillColor = [250, 219, 216];
                            tableData.cell.styles.textColor = [123, 36, 28];
                            tableData.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            });

            // Save Document File
            pdf.save(`${hospitalName.replace(/\s+/g, '_')}_MedicalDeviceRiskChecklist.pdf`);
        }
    }));

    return null;
});

export default ViewAssetMedicalRiskChecklist;